import { API_URL } from "../_config/config.const";
import { handleResponse } from "./handleResponse.helper";
import { sendScrollMessageToParent } from "./iFrameSize.helper";

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const CONTAINER_ID = "lq-turnstile";
const WIDGET_MOUNT_ID = "lq-turnstile-widget";
const LABEL_ID = "lq-turnstile-label";

type TurnstileApi = {
    render: (container: string | HTMLElement, options: Record<string, unknown>) => string;
    execute: (widgetId: string) => void;
    reset: (widgetId: string) => void;
    remove: (widgetId: string) => void;
};

declare global {
    interface Window {
        turnstile?: TurnstileApi;
    }
}

let widgetId: string | null = null;
let siteKeyPromise: Promise<string> | null = null;
let tokenQueue: Promise<string> = Promise.resolve("");
let pendingResolve: ((token: string) => void) | null = null;
let pendingReject: ((error: Error) => void) | null = null;
let didRetryAfterError = false;

function hideTurnstileContainer(container: HTMLElement) {
    const label = document.getElementById(LABEL_ID);
    if (label) label.style.display = "none";

    container.style.position = "fixed";
    container.style.left = "0";
    container.style.top = "0";
    container.style.right = "";
    container.style.bottom = "auto";
    container.style.transform = "";
    container.style.width = "300px";
    container.style.minWidth = "";
    container.style.minHeight = "65px";
    container.style.height = "65px";
    container.style.overflow = "hidden";
    container.style.opacity = "0";
    container.style.pointerEvents = "none";
    container.style.zIndex = "0";
    container.style.display = "block";
    container.style.flexDirection = "";
    container.style.alignItems = "";
    container.style.justifyContent = "";
    container.style.padding = "0";
    container.style.background = "";
    container.style.boxShadow = "";
    container.style.borderRadius = "";
}

function showTurnstileContainer(container: HTMLElement) {
    let label = document.getElementById(LABEL_ID);
    if (!label) {
        label = document.createElement("div");
        label.id = LABEL_ID;
        label.textContent = "Please verify to continue";
        label.style.fontSize = "14px";
        label.style.marginBottom = "8px";
        label.style.color = "#333";
        container.insertBefore(label, container.firstChild);
    }

    label.style.display = "block";
    container.style.position = "fixed";
    container.style.left = "50%";
    container.style.top = "120px";
    container.style.right = "auto";
    container.style.bottom = "auto";
    container.style.transform = "translateX(-50%)";
    container.style.width = "auto";
    container.style.minWidth = "300px";
    container.style.minHeight = "65px";
    container.style.height = "auto";
    container.style.overflow = "visible";
    container.style.opacity = "1";
    container.style.pointerEvents = "auto";
    container.style.zIndex = "1400";
    container.style.display = "flex";
    container.style.flexDirection = "column";
    container.style.alignItems = "center";
    container.style.justifyContent = "center";
    container.style.padding = "16px";
    container.style.background = "#fff";
    container.style.boxShadow = "0 4px 24px rgba(0,0,0,0.18)";
    container.style.borderRadius = "8px";
    sendScrollMessageToParent();
}

function ensureContainer(): HTMLElement {
    const existing = document.getElementById(CONTAINER_ID);
    const container = existing ?? document.createElement("div");
    if (!existing) {
        container.id = CONTAINER_ID;
        document.body.appendChild(container);
        hideTurnstileContainer(container);
    }

    return container;
}

function ensureWidgetMount(): HTMLElement {
    const root = ensureContainer();
    let mount = document.getElementById(WIDGET_MOUNT_ID);
    if (mount) {
        mount.innerHTML = "";
        return mount;
    }

    mount = document.createElement("div");
    mount.id = WIDGET_MOUNT_ID;
    root.appendChild(mount);

    return mount;
}

function destroyWidget() {
    if (widgetId && window.turnstile) {
        try { window.turnstile.remove(widgetId); } catch (_) { /* ignore */ }
    }

    widgetId = null;
    const mount = document.getElementById(WIDGET_MOUNT_ID);
    if (mount) mount.innerHTML = "";
}

function hideAndDestroy() {
    destroyWidget();
    hideTurnstileContainer(ensureContainer());
}

function loadTurnstileSiteKey(): Promise<string> {
    if (siteKeyPromise) return siteKeyPromise;

    siteKeyPromise = fetch(`${API_URL}/form/turnstileSiteKey`)
        .then(handleResponse)
        .then((data) => {
            const siteKey = String(data?.siteKey ?? data?.SiteKey ?? "");
            if (!siteKey) throw new Error("Captcha is not configured.");
            return siteKey;
        })
        .catch((err) => {
            siteKeyPromise = null;
            throw err;
        });

    return siteKeyPromise;
}

function loadTurnstileScript(): Promise<TurnstileApi> {
    if (window.turnstile) return Promise.resolve(window.turnstile);

    return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src^="https://challenges.cloudflare.com/turnstile"]`) as HTMLScriptElement | null;
        if (existing) {
            if (window.turnstile) {
                resolve(window.turnstile);
                return;
            }
            existing.addEventListener("load", () => resolveTurnstile(resolve, reject));
            existing.addEventListener("error", () => reject(new Error("Captcha failed to load")));
            return;
        }

        const script = document.createElement("script");
        script.src = TURNSTILE_SCRIPT_SRC;
        script.onload = () => resolveTurnstile(resolve, reject);
        script.onerror = () => reject(new Error("Captcha failed to load"));
        document.head.appendChild(script);
    });
}

function resolveTurnstile(resolve: (api: TurnstileApi) => void, reject: (error: Error) => void) {
    if (window.turnstile) {
        resolve(window.turnstile);
        return;
    }

    reject(new Error("Captcha failed to load"));
}

function rejectPending(message: string) {
    hideAndDestroy();
    if (pendingReject) pendingReject(new Error(message));
    pendingResolve = null;
    pendingReject = null;
}

function showWidgetAfterChallengeNeeded() {
    showTurnstileContainer(ensureContainer());
}

function retryVisibleAfterError() {
    showWidgetAfterChallengeNeeded();
    if (didRetryAfterError || !widgetId || !window.turnstile) {
        rejectPending("Captcha validation failed.");
        return;
    }

    didRetryAfterError = true;
    try { window.turnstile.reset(widgetId); } catch (_) { /* ignore */ }
    try { window.turnstile.execute(widgetId); } catch (_) { /* ignore */ }
}

function resolvePending(token: string) {
    hideAndDestroy();
    if (pendingResolve) pendingResolve(token);
    pendingResolve = null;
    pendingReject = null;
}

function renderWidget(turnstile: TurnstileApi, siteKey: string): string {
    destroyWidget();

    const root = ensureContainer();
    const mount = ensureWidgetMount();
    hideTurnstileContainer(root);
    didRetryAfterError = false;

    widgetId = turnstile.render(mount, {
        sitekey: siteKey,
        appearance: "interaction-only",
        execution: "execute",
        size: "normal",
        callback: (token: string) => resolvePending(token),
        "before-interactive-callback": () => showWidgetAfterChallengeNeeded(),
        "error-callback": () => retryVisibleAfterError(),
        "expired-callback": () => rejectPending("Captcha expired. Please try again."),
        "timeout-callback": () => rejectPending("Captcha timed out. Please try again.")
    });

    return widgetId;
}

function requestNewToken(): Promise<string> {
    return loadTurnstileSiteKey().then((siteKey) => {
        return loadTurnstileScript().then((turnstile) => {
            return new Promise<string>((resolve, reject) => {
                pendingResolve = resolve;
                pendingReject = reject;
                const id = renderWidget(turnstile, siteKey);
                turnstile.execute(id);
            });
        });
    });
}

export function getTurnstileToken(): Promise<string> {
    tokenQueue = tokenQueue.then(() => requestNewToken(), () => requestNewToken());
    return tokenQueue;
}
